/**
 * 倒计时工具类
 * 用于验证码发送等场景的倒计时管理
 */

/** 倒计时配置 */
export interface CountdownOptions {
  /** 倒计时秒数，默认 60 */
  seconds?: number
  /** 每秒回调 */
  onTick?: (remaining: number) => void
  /** 结束回调 */
  onEnd?: () => void
  /** 是否自动开始，默认 false */
  autoStart?: boolean
}

/** 倒计时状态 */
export interface CountdownState {
  /** 剩余秒数 */
  remaining: number
  /** 是否正在运行 */
  isRunning: boolean
  /** 是否已结束 */
  isEnded: boolean
}

/**
 * 倒计时类
 */
export class Countdown {
  private seconds: number
  private remaining: number
  private timerId: ReturnType<typeof setInterval> | null = null
  private isRunning = false
  private onTick?: (remaining: number) => void
  private onEnd?: () => void

  constructor(options: CountdownOptions | number = {}) {
    // 支持直接传入秒数
    if (typeof options === 'number') {
      this.seconds = options
      this.remaining = options
    } else {
      this.seconds = options.seconds ?? 60
      this.remaining = this.seconds
      this.onTick = options.onTick
      this.onEnd = options.onEnd

      if (options.autoStart) {
        this.start()
      }
    }
  }

  /**
   * 开始倒计时
   */
  start(seconds?: number): void {
    // 如果已经在运行，先停止
    if (this.isRunning) {
      this.stop()
    }

    // 设置倒计时秒数
    if (seconds !== undefined) {
      this.seconds = seconds
    }
    this.remaining = this.seconds
    this.isRunning = true

    // 立即触发一次
    this.onTick?.(this.remaining)

    // 开始定时器
    this.timerId = setInterval(() => {
      this.remaining--

      // 触发 tick 回调
      this.onTick?.(this.remaining)

      // 检查是否结束
      if (this.remaining <= 0) {
        this.stop()
        this.onEnd?.()
      }
    }, 1000)
  }

  /**
   * 停止倒计时
   */
  stop(): void {
    if (this.timerId) {
      clearInterval(this.timerId)
      this.timerId = null
    }
    this.isRunning = false
  }

  /**
   * 重置倒计时
   */
  reset(seconds?: number): void {
    this.stop()
    if (seconds !== undefined) {
      this.seconds = seconds
    }
    this.remaining = this.seconds
  }

  /**
   * 重新开始（重置并开始）
   */
  restart(seconds?: number): void {
    this.reset(seconds)
    this.start()
  }

  /**
   * 获取剩余秒数
   */
  getRemaining(): number {
    return this.remaining
  }

  /**
   * 检查是否正在运行
   */
  getIsRunning(): boolean {
    return this.isRunning
  }

  /**
   * 检查是否可以开始（未运行且剩余为 0 或等于初始值）
   */
  canStart(): boolean {
    return !this.isRunning && (this.remaining === 0 || this.remaining === this.seconds)
  }

  /**
   * 获取当前状态
   */
  getState(): CountdownState {
    return {
      remaining: this.remaining,
      isRunning: this.isRunning,
      isEnded: this.remaining === 0 && !this.isRunning
    }
  }

  /**
   * 设置回调
   */
  setCallbacks(callbacks: { onTick?: (remaining: number) => void; onEnd?: () => void }): void {
    if (callbacks.onTick) {
      this.onTick = callbacks.onTick
    }
    if (callbacks.onEnd) {
      this.onEnd = callbacks.onEnd
    }
  }

  /**
   * 销毁实例
   */
  destroy(): void {
    this.stop()
    this.onTick = undefined
    this.onEnd = undefined
  }
}

/**
 * 创建倒计时实例
 */
export function createCountdown(options: CountdownOptions | number = {}): Countdown {
  return new Countdown(options)
}
